import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { VALIDATION_FIELD_MAPPING, APP_SUCCESS_MESSAGES } from '../../../../utils/appConstant';
import Input from '../../../../components/Input';
import Button from '../../../../components/Button';
import Link from '../../../../components/Link';
import Toast from '../../../../components/Toast';
import AuthService from '../../../../services/auth.service';
import { Validation } from '../../../../interfaces/common';
import { ForgotPassword } from '../../../../interfaces/auth.interface';
import { Modules, NonAuthRoutes } from '../../../../enums';
import { validateForgotPasswordForm } from '../validations';

const Form: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [formData, setFormData] = useState<ForgotPassword>({ email: '' });
  const [loading, setLoading] = useState(false);
  const [validation, setValidation] = useState<Validation>({ validate: true, fields: [] });

  const handleInputChange = useCallback(
    (event: any, name: string) => {
      setFormData((prev) => {
        return {
          ...prev,
          [name]: event.target.value,
        };
      });
    },
    [setFormData]
  );

  const handleInputBlur = useCallback(
    (event: any, name: string) => {
      let newValidation = validateForgotPasswordForm(
        {
          ...formData,
          [name]: event.target.value.trim(),
        },
        name
      );
      setValidation(newValidation);
    },
    [formData.email]
  );

  const handleSubmit = useCallback(
    async (event?: React.FormEvent) => {
      event && event.preventDefault();

      const newValidation = validateForgotPasswordForm(formData);
      setValidation(newValidation);

      if (!newValidation.validate) return;

      setLoading(true);

      try {
        const data: any = await AuthService.forgotPassword({ email: formData.email.trim() });

        if (data.success !== false) {
          toast.success(<Toast message={APP_SUCCESS_MESSAGES.forgotPassword} />, { icon: false });
          setLoading(false);
        }
      } catch (err: any) {
        setLoading(false);
      }
    },
    [formData.email]
  );

  const handleKeyDown = useCallback(
    (e: any) => {
      if (e.key === 'Enter') handleSubmit(e);
    },
    [handleSubmit]
  );

  const handleLogin = () => {
    navigate(Modules.auth);
  };

  return (
    <form id="formAuthentication" className="mb-5" autoComplete="off">
      <div className="form-floating form-floating-outline mb-5">
        <Input
          type="text"
          id={VALIDATION_FIELD_MAPPING.email}
          name={VALIDATION_FIELD_MAPPING.email}
          error={
            !!validation.fields.find((field) => field.field === VALIDATION_FIELD_MAPPING.email)
          }
          errorMessage={
            validation.fields.find((field) => field.field === VALIDATION_FIELD_MAPPING.email)
              ?.message ?? ''
          }
          placeholder={t('forgotPassword.pageTexts.form.email.placeholder')}
          label={t('forgotPassword.pageTexts.form.email.label')}
          value={formData.email}
          onKeyDown={handleKeyDown}
          onBlur={handleInputBlur}
          onChange={handleInputChange}
        />
      </div>
      <Button
        className="btn btn-primary d-grid w-100"
        text={t('forgotPassword.pageTexts.form.sendResetLinkButton')}
        disabled={loading}
        loading={loading}
        onClick={handleSubmit}
      />
      <div className="text-center mt-4">
        <Link
          className="hyperlink d-flex align-items-center justify-content-center"
          text={t('forgotPassword.pageTexts.form.backToLoginLink')}
          icon={<i className="ri-arrow-left-s-line scaleX-n1-rtl ri-20px me-1_5"></i>}
          onClick={handleLogin}
        />
      </div>
    </form>
  );
};

export default Form;
